import { extractVideoThumbnail } from ".";

/**
 * Handles a drop event or an array of files and calls your callback with the files.
 * @param {Event|Array} e - The drop event or array of files
 * @param {Function} onFiles - Callback to handle the files (array)
 */
// Handle drop 
export const handleDropUtil = (e, onFiles) => {
    const event = e.nativeEvent ? e.nativeEvent : e;
    if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
        // Try both event.dataTransfer and event.nativeEvent.dataTransfer for drag and file upload
        const dataTransfer = event.dataTransfer || (event.nativeEvent && event.nativeEvent.dataTransfer);
        const files = dataTransfer ? Array.from(dataTransfer.files) : [];
        if (files.length > 0) {
            onFiles(files);
        }
    } else if (Array.isArray(e)) {
        onFiles(e);
    }
};

// Handle copy paste image
export const handlePaste = (event, setAttachments) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    const pastedImages = [];

    for (const item of items) {
        if (item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (!file) continue;

            const url = URL.createObjectURL(file);
            pastedImages.push({
                name: file.name || "pasted-image.png",
                type: file.type,
                url,
                file,
                loading: false,
            });
        }
    }

    if (pastedImages.length > 0) {
        setAttachments(prev => [...prev, ...pastedImages]);
    }
};

export const getFilePreviews = async (files) => {
    if (!files || files.length === 0) return [];

    const newPreviewsPromises = Array.from(files).map(file => {
        const isImage = file.type.startsWith("image/");
        const isVideo = file.type.startsWith("video/");

        const clonedFile = new File([file], file.name, { type: file.type });

        if (isImage) {
            return new Promise(resolve => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve({
                        url: reader.result,
                        type: clonedFile.type,
                        name: clonedFile.name,
                        file: clonedFile,
                    });
                };
                reader.readAsDataURL(clonedFile);
            });
        } else if (isVideo) {
            return extractVideoThumbnail(clonedFile, { asBlob: false })
                .then(thumbnailUrl => ({
                    url: thumbnailUrl,
                    type: clonedFile.type,
                    name: clonedFile.name,
                    videoUrl: URL.createObjectURL(clonedFile),
                    file: clonedFile,
                }));
        } else {
            return Promise.resolve({
                url: null,
                type: clonedFile.type,
                name: clonedFile.name,
                file: clonedFile,
            });
        }
    });

    return Promise.all(newPreviewsPromises);
};

export const mxcToHttp = (mxcUrl, baseUrl) => {
    if (!mxcUrl || typeof mxcUrl !== "string") {
        if (mxcUrl) console.warn("Unexpected mxcUrl value:", mxcUrl);
        return "";
    }
    const [ , server, mediaId ] = mxcUrl.match(/^mxc:\/\/([^/]+)\/(.+)$/) || [];
    if (!server || !mediaId) return "";
    return `${baseUrl}/_matrix/media/v3/download/${encodeURIComponent(server)}/${encodeURIComponent(mediaId)}`;

};

export const getAuthenticatedMediaUrl = (mxcUrl, matrixClient) => {
    if (!mxcUrl || !matrixClient) {
        return null;
    }
    return matrixClient.mxcUrlToHttp(mxcUrl, undefined, undefined, undefined, false, true, true);
};

export const fetchMatrixMedia = async (mxcUrl, matrixClient, signal) => {
    const mediaUrl = getAuthenticatedMediaUrl(mxcUrl, matrixClient);
    const accessToken = matrixClient?.getAccessToken?.();
    if (!mediaUrl || !accessToken) {
        throw new Error("Authenticated Matrix media is unavailable.");
    }

    const response = await fetch(mediaUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
        redirect: "follow",
        signal,
    });
    if (!response.ok) {
        throw new Error(`Matrix media download failed with status ${response.status}.`);
    }
    return response;
};


export const getImageDimensions = (file) =>
    new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(null);
        };
        img.src = objectUrl;
    });

/**
 * Matrix End-to-End Encryption:
 * See Matrix Spec for E2EE:
 * https://spec.matrix.org/v1.14/client-server-api/#end-to-end-encryption
 *
 * Encrypted Attachments:
 * Files are encrypted client-side before uploading, then uploaded as ciphertext.
 * The payload includes encryption metadata (key, iv, hashes) per Matrix spec.
 * 
 * See Matrix Spec for Encrypted Attachments:
 * https://spec.matrix.org/v1.14/client-server-api/#sending-encrypted-attachments
 *
 * Crypto details:
 * - AES-CTR is used (alg: "A256CTR") for symmetric encryption
 * - Initialization Vector (iv) used for randomness
 * - SHA-256 hash ensures integrity verification
 */

export const encryptFile = async (file) => {
    const iv = crypto.getRandomValues(new Uint8Array(16));
    const key = await crypto.subtle.generateKey(
        { name: "AES-CTR", length: 256 },
        true,
        ["encrypt", "decrypt"]
    );
    const raw = await file.arrayBuffer();
    const cipher = await crypto.subtle.encrypt(
        { name: "AES-CTR", counter: iv, length: 64 },
        key,
        raw
    );

    const keyJwk = await crypto.subtle.exportKey("jwk", key);
    const hashBuf = await crypto.subtle.digest("SHA-256", cipher);
    const ciphertext = new Blob([cipher], { type: file.type });

    return { ciphertext, iv, keyJwk, hash: hashBuf };
};

export async function decryptEncryptedFile(fileContent, encryptedFile, mimetype) {
    if (
        encryptedFile?.v !== "v2" ||
        encryptedFile?.key?.alg !== "A256CTR" ||
        typeof encryptedFile?.iv !== "string" ||
        typeof encryptedFile?.hashes?.sha256 !== "string"
    ) {
        throw new Error("Encrypted Matrix media metadata is invalid.");
    }

    const buffer = await fileContent.arrayBuffer();
    const expectedHash = base64Decode(encryptedFile.hashes.sha256);
    const actualHash = new Uint8Array(await crypto.subtle.digest("SHA-256", buffer));
    if (!equalBytes(expectedHash, actualHash)) {
        throw new Error("Encrypted Matrix media failed its integrity check.");
    }

    const key = await crypto.subtle.importKey(
        "jwk",
        encryptedFile.key,
        { name: "AES-CTR", length: 256 },
        false,
        ["decrypt"]
    );

    const decrypted = await crypto.subtle.decrypt(
        { name: "AES-CTR", counter: base64Decode(encryptedFile.iv), length: 64 },
        key,
        buffer
    );

    return new Blob([decrypted], { type: mimetype || "application/octet-stream" });
}

function base64Decode(input) {
    const normalisedInput = input.replace(/-/g, "+").replace(/_/g, "/");
    const paddedInput = normalisedInput.padEnd(Math.ceil(normalisedInput.length / 4) * 4, "=");
    const raw = atob(paddedInput);
    const result = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) {
        result[i] = raw.charCodeAt(i);
    }
    return result;
}

function equalBytes(first, second) {
    if (first.length !== second.length) {
        return false;
    }
    let difference = 0;
    for (let index = 0; index < first.length; index += 1) {
        difference |= first[index] ^ second[index];
    }
    return difference === 0;
}
